// spell-checker: enableCompoundWords
import { CategoryHeaderData } from '../category-header/category-header.component';
import { Location, PopStateEvent } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { GetProductsByCategoryOptions } from '@app/api/Product';
import { getProducts, getProductsByCategory } from '@app/api/action/Product';
import { Pagination, RootReducer, Store } from '@app/app.reducers';
import {
	getCategories,
	getGenericCategoryTree,
	getSelectedCategory,
	setSelectedCategory,
} from '@app/category/reducers/category.reducer';
import { getPaginatedProducts, isLoading } from '@app/product/reducers/product.reducer';
import { SortChangeEvent, SortOption } from '@app/shared/components/sorter/sorter.component';
import { getStorefront } from '@app/shared/reducers/storefront.reducer';
import { DataLayer } from '@app/shared/services/data-layer.service';
import { SeoService } from '@app/shared/services/seo.service';
import { ViewportService } from '@app/shared/services/viewport.service';
import { I18nService } from '@app/shared/utils/i18n.service';
import { filterTruthy, firstTruthy } from '@app/shared/utils/util';
import { getUserPermissions } from '@app/user/reducers/user.reducer';
import { faArrowDownAZ } from '@fortawesome/pro-solid-svg-icons/faArrowDownAZ';
import { faArrowUpAZ } from '@fortawesome/pro-solid-svg-icons/faArrowUpAZ';
import { faList } from '@fortawesome/pro-solid-svg-icons/faList';
import { faSort } from '@fortawesome/pro-solid-svg-icons/faSort';
import { faTableCellsLarge } from '@fortawesome/pro-solid-svg-icons/faTableCellsLarge';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, merge, Observable, of as observableOf } from 'rxjs';
import { distinctUntilChanged, filter, map, skip, switchMap, tap, withLatestFrom } from 'rxjs/operators';

export type CategoryInfo = api.CategoryDto & { isExpanded?: boolean; children: CategoryInfo[] };

export function getCategoryIdList(category: api.CategoryDto): number[] {
	let categoryIds = [category.id];
	if (Array.isArray(category.children) && category.children.length) {
		for (const child of category.children) {
			categoryIds = [...categoryIds, ...getCategoryIdList(child)];
		}
	}
	return categoryIds;
}

export enum ProductView {
	Thumbnail = 'thumbnails',
	List = 'list',
}

export const DEFAULT_PAGE_SIZE = 9;

@UntilDestroy()
@Component({
	selector: 'g-category',
	templateUrl: './category.component.html',
	styleUrls: ['./category.component.scss'],
})
export class CategoryComponent implements OnInit {
	@ViewChild('scrollToOnPageChange') readonly scrollToElementOnPageChange!: ElementRef<HTMLDivElement>;
	mainCategories: CategoryInfo[] = [];
	allCategories: CategoryInfo[] = [];
	filteredProducts$ = observableOf<api.ProductDto[]>([]);
	categoryHeaderData$!: Observable<CategoryHeaderData>;
	currentProductView?: string;
	sortBy?: SortOption;
	sortDirection?: number;
	sortOptions?: SortOption[];
	currentPage?: number;
	pageSize = DEFAULT_PAGE_SIZE;
	pageCount?: number;
	isLoading?: boolean;
	readonly icons = {
		faList,
		faTableCellsLarge,
	};
	productView = ProductView; // For template
	private category?: CategoryInfo;

	// Default values for query params that are not present
	private readonly queryParamDefaults = {
		page: 1,
		sortDir: 1,
		sortBy: 'name',
	};

	// To make unit testing easier
	private readonly localStorage = localStorage;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly store: Store<RootReducer.State>,
		private readonly dataLayer: DataLayer,
		private readonly seo: SeoService,
		private readonly i18n: I18nService,
		private readonly location: Location,
		public readonly viewport: ViewportService,
	) {}

	readonly showPrices$ = this.store
		.select(getUserPermissions)
		.pipe(map((permissions) => permissions?.show_prices ?? true));

	private get showAllProducts() {
		return !this.category;
	}

	ngOnInit(): void {
		this.setupSortOptions();

		// Wrap location.subscribe in an observable for proper rxjs functionality.
		const historyPopState$ = new Observable((sub) =>
			this.location.subscribe(sub.next.bind(sub), sub.error.bind(sub), sub.complete.bind(sub)),
		);

		const searchParams$ = merge(
			// PopState events
			historyPopState$.pipe(
				map((val: PopStateEvent) => new URLSearchParams((val.url || '').split('?')[1])),
			),
			// QueryParams on route change.
			this.route.queryParams.pipe(
				map((initialQueryParams) => new Map(Object.entries(initialQueryParams))),
			),
		);

		// Handle all but the initial query params
		searchParams$.pipe(skip(1), untilDestroyed(this)).subscribe((queryParams) => {
			this.handleQueryParams(queryParams);
			this.fetchProducts();
		});

		combineLatest([
			this.route.params.pipe(withLatestFrom(searchParams$)),
			this.store.select(getStorefront).pipe(firstTruthy),
			this.store.select(getGenericCategoryTree(this.i18n)).pipe(firstTruthy),
			this.store.select(getCategories).pipe(firstTruthy),
		])
			.pipe(
				distinctUntilChanged(),
				// For async await.
				switchMap(async ([[params, queryParams], storefront, categoryTree, allCategories]) => {
					this.allCategories = structuredClone(allCategories);
					await this.handleParams(params, storefront, structuredClone(categoryTree as CategoryInfo[]));
					this.handleQueryParams(queryParams);
					this.fetchProducts();
				}),
				untilDestroyed(this),
			)
			.subscribe();

		this.store
			.select(getSelectedCategory)
			.pipe(filterTruthy, untilDestroyed(this))
			.subscribe((category) => {
				this.setupCategoryHeader(category);
				this.seo.deduceProperties(category);
			});

		this.filteredProducts$ = this.store.select(getPaginatedProducts).pipe(
			map(
				(paginatedProducts) =>
					paginatedProducts?.[this.getPaginationKeyForCurrentView()]?.products ?? [],
			),
			tap((products) => this.showAllProducts || this.addImpressions(products)),
			untilDestroyed(this),
		);

		this.store
			.select(getPaginatedProducts)
			.pipe(
				map(
					(paginatedProducts) => paginatedProducts?.[this.getPaginationKeyForCurrentView()]?.pagination,
				),
				filter<Pagination>(Boolean), // Only handle valid pagination configurations
				map((pagination) => pagination.pageCount),
				distinctUntilChanged(),
				untilDestroyed(this),
			)
			.subscribe((pageCount) => {
				this.pageCount = pageCount;

				// If the selected page is out of range, go to page 1.
				if (this.currentPage > pageCount || this.currentPage < 1) {
					this.onPageChange(1);
				}
			});

		this.store
			.select(isLoading)
			.pipe(untilDestroyed(this))
			.subscribe((status) => {
				this.isLoading = status;
			});
	}

	onPageChange(currentPage: number): void {
		this.currentPage = currentPage;
		this.scrollToTop();
		this.updateQueryString({ page: currentPage });
		this.fetchProducts();
	}

	setSort(selected: SortChangeEvent): void {
		this.sortBy = selected.value;
		this.sortDirection = selected.direction;
		this.updateQueryString({
			sortBy: selected.value.column,
			sortDir: selected.direction,
		});
		this.fetchProducts();
	}

	setView(viewType: ProductView): void {
		this.currentProductView = viewType;
		this.localStorage.setItem('productView', viewType);
	}

	selectCategory(category?: CategoryInfo): void {
		this.currentPage = 1;
		this.category = category;
		this.store.dispatch(setSelectedCategory(structuredClone(category)));
		this.expandCategories(category);
	}

	getBasicHeader(): string {
		if (!this.category) return '';
		const categoryHeader = this.category?.metadata?.categoryheader as api.LocaleStringDto;
		if (categoryHeader) {
			const categoryHeaderText = this.i18n.getText(categoryHeader);
			if (categoryHeaderText) return categoryHeaderText;
		}
		const categoryName = this.category?.locale?.name;
		return this.i18n.getText(categoryName);
	}

	private handleQueryParams(queryParams: Map<string, string> | URLSearchParams) {
		const page = +queryParams.get('page') || this.queryParamDefaults.page;
		this.currentPage = page;

		const sortBy = queryParams.get('sortBy') || this.queryParamDefaults.sortBy;
		const sortOption = this.sortOptions?.find((option) => option.column === sortBy);
		if (sortOption) {
			this.sortBy = sortOption;
		}

		const sortDir = queryParams.get('sortDir') || this.queryParamDefaults.sortDir;
		this.sortDirection = +sortDir;
	}

	private async handleParams(params: Params, storefront: api.StoreDto, categories: CategoryInfo[]) {
		const categoryId = Number.parseInt(params.id as string);
		this.mainCategories = this.filterMainCategories(categories);
		const category = this.findCategory(this.mainCategories, categoryId);
		this.setupProductView(storefront);
		if (category) {
			this.selectCategory(category);
			if (params.name === undefined) {
				await this.router.navigate(['category', categoryId, category.slug]); // Add slug to URL if it is missing
			}
		} else {
			// Show all products
			this.selectCategory();
			this.seo.setDirectly({ title: $localize`:@@SeoTitleProducts:Products` });
		}
	}

	private generateCategoryIdString(category: api.CategoryDto) {
		return getCategoryIdList(category).join(',');
	}

	private getCurrentCategoryKey() {
		return this.showAllProducts ? 'all' : this.generateCategoryIdString(this.category);
	}

	private getPaginationKeyForCurrentView() {
		const { sortBy, sortDir, sortLang, page } = this.getCurrentViewParams();
		return [this.getCurrentCategoryKey(), sortLang, page, sortBy, sortDir].join('&');
	}

	private getCurrentViewParams() {
		return {
			sortBy: this.sortBy.column as GetProductsByCategoryOptions['sortBy'],
			sortDir: this.sortDirection ?? 1,
			sortLang: this.i18n.locale,
			page: this.currentPage,
			pageSize: this.pageSize,
		};
	}

	private fetchProducts(): void {
		if (this.showAllProducts) {
			this.fetchAllProducts();
		} else {
			this.fetchCategoryProducts(this.category);
		}
	}

	private fetchCategoryProducts(category: api.CategoryDto): void {
		const categoryIds = this.generateCategoryIdString(category);
		this.store.dispatch(
			getProductsByCategory(categoryIds, this.getCurrentViewParams(), {
				collectionKey: this.getPaginationKeyForCurrentView(),
			}),
		);
	}

	private fetchAllProducts(): void {
		this.store.dispatch(
			getProducts(this.getCurrentViewParams(), {
				collectionKey: this.getPaginationKeyForCurrentView(),
			}),
		);
	}

	private setupProductView(storefront: api.StoreDto) {
		// Get initial value from local storage or storefront settings
		this.setView(
			(this.localStorage.getItem('productView') as ProductView) ||
				((storefront?.storefront?.default_product_view ?? ProductView.Thumbnail) as ProductView),
		);
	}

	private setupSortOptions(): void {
		this.sortOptions = [
			{
				label: $localize`:@@CategorySortByName:Sort by Name`,
				column: 'name',
				icons: {
					neutral: faSort,
					asc: faArrowDownAZ,
					desc: faArrowUpAZ,
				},
			},
			{
				label: $localize`:@@CategorySortByDate:Sort by Date Added`,
				column: 'created_at',
			},
		];

		this.sortBy = this.sortOptions[0];
	}

	private setupCategoryHeader(category: api.CategoryDto): void {
		const metadata = category?.metadata || {};
		const hasImage = !!this.i18n.getText(category.images);
		this.categoryHeaderData$ = observableOf({
			isEnabled: Boolean(metadata.categorydescription) || hasImage,
			header: metadata.categoryheader,
			body: metadata.categorybody,
			image: category.images,
		});
	}

	private addImpressions(products: api.ProductDto[]): void {
		this.dataLayer.addImpressions(products, DataLayer.CATEGORY_LISTING);
	}

	private filterMainCategories(categories: CategoryInfo[]): CategoryInfo[] {
		return categories.filter((category) => category.path?.split('.').length === 2);
	}

	/**
	 * Recursively finds the category in the categories array
	 */
	private findCategory(categories: CategoryInfo[], categoryId: number): CategoryInfo | undefined {
		let result: CategoryInfo | undefined;
		for (const category of categories) {
			if (category.id === categoryId) return category;
			/* istanbul ignore else */
			if (Array.isArray(category.children)) {
				result = this.findCategory(category.children, categoryId);
				if (result !== undefined) return result;
			}
		}
		return result;
	}

	private scrollToTop() {
		this.scrollToElementOnPageChange.nativeElement.scrollIntoView({ behavior: 'smooth' });
	}

	/** Keeps the correct category structure expanded in the view */
	private expandCategories(category?: CategoryInfo): void {
		this.closeAllCategories(this.mainCategories);
		if (!category) return;
		this.expandParentCategories(category?.path!.split('.'), this.mainCategories);
	}

	/** Recursively sets isExpanded to false for every category */
	private closeAllCategories(categories: CategoryInfo[]): void {
		for (const category of categories) {
			// TODO: Move isExpanded or add it to the type.
			category.isExpanded = false;
			/* istanbul ignore else */
			if (Array.isArray(category.children)) {
				this.closeAllCategories(category.children);
			}
		}
	}

	/**
	 * Recursively goes through the categories and expands them if needed
	 */
	private expandParentCategories(categoryNames: string[], categories: CategoryInfo[]): void {
		for (const category of categories) {
			category.isExpanded = categoryNames.includes(category.path!.split('.').pop()!);
			/* istanbul ignore else */
			if (Array.isArray(category.children)) {
				this.expandParentCategories(categoryNames, category.children);
			}
		}
	}

	// TODO: Move to a service / utils
	private updateQueryString(updates: Record<string, string | number>) {
		const [path, queryString] = this.location.path().split('?');
		const queryParams = new URLSearchParams(queryString);

		let shouldUpdateUrl = false;
		for (const [key, val] of Object.entries(updates)) {
			if (queryParams.get(key) !== `${val}`) shouldUpdateUrl = true;
			queryParams.set(key, `${val}`);
		}

		if (shouldUpdateUrl) {
			this.location.go(path, queryParams.toString());
		}
	}
}
